// Hit-testing / geometry helpers (all in pixel space).

export function distToSegment(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  const cx = x1 + t * dx;
  const cy = y1 + t * dy;
  return Math.hypot(px - cx, py - cy);
}

// distance to an infinite line through (x1,y1)-(x2,y2)
export function distToLine(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len === 0) return Math.hypot(px - x1, py - y1);
  return Math.abs((px - x1) * dy - (py - y1) * dx) / len;
}

export function pointNear(px: number, py: number, x: number, y: number, tol: number): boolean {
  return Math.abs(px - x) <= tol && Math.abs(py - y) <= tol;
}

export function pointInRect(px: number, py: number, x1: number, y1: number, x2: number, y2: number): boolean {
  const lx = Math.min(x1, x2);
  const rx = Math.max(x1, x2);
  const ty = Math.min(y1, y2);
  const by = Math.max(y1, y2);
  return px >= lx && px <= rx && py >= ty && py <= by;
}

// distance to rectangle border (for hollow-rect hit testing)
export function distToRectBorder(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
  const lx = Math.min(x1, x2),
    rx = Math.max(x1, x2),
    ty = Math.min(y1, y2),
    by = Math.max(y1, y2);
  return Math.min(
    distToSegment(px, py, lx, ty, rx, ty),
    distToSegment(px, py, rx, ty, rx, by),
    distToSegment(px, py, rx, by, lx, by),
    distToSegment(px, py, lx, by, lx, ty)
  );
}

export function distToEllipse(px: number, py: number, cx: number, cy: number, rx: number, ry: number): number {
  if (rx <= 0 || ry <= 0) return Infinity;
  // approximate normalized distance to ellipse boundary
  const nx = (px - cx) / rx;
  const ny = (py - cy) / ry;
  const d = Math.sqrt(nx * nx + ny * ny);
  return Math.abs(d - 1) * Math.min(rx, ry);
}

export function withAlpha(hex: string, alpha: number): string {
  // hex like #rrggbb
  if (hex.startsWith("#") && hex.length === 7) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
  return hex;
}
